// Parent progress dashboard. Pick a child → advanced per-kid view:
// time spent, streak, XP + sparkline, lessons + track %, 12-week activity
// heatmap, per-subject breakdown, most-played activity types.
// docs/replan-v2.md R5.

import { useT } from '@fajrarabi/i18n/react';
import { Card, CardBody, Skeleton } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { ProgressDashboard } from '~/components/progress/ProgressDashboard';
import { useAuth } from '~/lib/auth';
import { listKids, type Kid } from '~/lib/family';
import { getKidProgress } from '~/lib/learn';
import { asLocale } from '~/lib/locale';
import { shellForContext } from '~/lib/roles';

export const Route = createFileRoute('/$locale/progress')({
  component: ProgressPanel,
});

function ProgressPanel() {
  const t = useT();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/progress' });
  const locale = asLocale(rawLocale);
  const { currentRoleContext } = useAuth();

  const isParent = shellForContext(currentRoleContext) === 'parent';
  useEffect(() => {
    if (!isParent) {
      void navigate({ to: '/$locale', params: { locale }, replace: true });
    }
  }, [isParent, navigate, locale]);

  const kidsQuery = useQuery({
    queryKey: ['family', 'kids'],
    queryFn: ({ signal }) => listKids(signal),
    enabled: isParent,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const kids = kidsQuery.data ?? [];
  const activeKid = kids.find((k) => k.id === selectedId) ?? kids[0] ?? null;

  if (!isParent) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">
        {activeKid
          ? t('app.progress.title', { name: activeKid.displayName ?? activeKid.username })
          : t('app.progress.pick_kid')}
      </h1>

      {kidsQuery.isLoading ? (
        <Skeleton className="mt-6 h-40 w-full" />
      ) : kids.length === 0 ? (
        <Card className="mt-6">
          <CardBody>
            <p className="text-ink-700">{t('app.progress.no_kids')}</p>
          </CardBody>
        </Card>
      ) : (
        <>
          {kids.length > 1 ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {kids.map((k) => (
                <KidTab
                  key={k.id}
                  kid={k}
                  active={k.id === (activeKid?.id ?? '')}
                  onClick={() => setSelectedId(k.id)}
                />
              ))}
            </div>
          ) : null}
          {activeKid ? <KidDashboard kidId={activeKid.id} /> : null}
        </>
      )}
    </div>
  );
}

function KidTab({ kid, active, onClick }: { kid: Kid; active: boolean; onClick: () => void }) {
  const name = kid.displayName ?? kid.username;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${
        active
          ? 'bg-burgundy-500 text-cream-50'
          : 'bg-cream-100 text-ink-700 ring-1 ring-burgundy-500/10 hover:bg-cream-200'
      }`}
    >
      {name}
    </button>
  );
}

function KidDashboard({ kidId }: { kidId: string }) {
  const t = useT();
  const query = useQuery({
    queryKey: ['parent', 'progress', kidId],
    queryFn: ({ signal }) => getKidProgress(kidId, signal),
  });

  if (query.isLoading) return <Skeleton className="mt-6 h-96 w-full" />;
  if (query.isError || !query.data) {
    return (
      <Card className="mt-6">
        <CardBody>
          <p className="text-ink-700">{t('app.progress.empty_activity')}</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="mt-6">
      <ProgressDashboard progress={query.data} />
    </div>
  );
}
